<template>
  <div>
    <div class="document">
      <div class="header">
        <EsHeader>
          <template #header>
            <div class="header-title">
              Near Match Processing Details
            </div>
          </template>
        </EsHeader>
          <div class="icons" v-if="!editCurrentPanel && !loading && !loadingError">
            <a role="button" tabindex="0"
                @keyup.enter="editPanel()"
                @click="editPanel()" v-if="hasModule('SYS:SYS:CONFIGMGR:EDIT')">
              <font-awesome-icon :icon="['far', 'edit']" size="lg" title="Edit" />
            </a>
            <a role="button" tabindex="0"
                @keyup.enter="goBackToNotifications()"
                @click="goBackToNotifications()">
              <font-awesome-icon :icon="['far', 'angle-double-left']" size="lg" title="Go Back" />
            </a>
          </div>
      </div>
      <es-loader v-if="loading" style="margin: 5rem"/>
      <es-exception
        v-else-if="loadingError"
        exceptionMessage="There was an issue. Please try again or contact your administrator."
        margin="0"
        style="padding: 5rem"
      />
      <div v-else class="content">
        <es-form style="padding: 1.15rem 0rem">
          <template #es-form-header>
            <div class="header-container">
              <div v-if="editCurrentPanel">
                <th>
                  <font-awesome-icon :icon="['fas', 'cog']" size="lg"/>
                    Edit Near Match Processing Details
                </th>
              </div>
              <div v-else>
                <th>
                  <font-awesome-icon :icon="['fas', 'cog']" size="lg"/>
                    Near Match Processing Details
                </th>
              </div>
            </div>
          </template>
          <template #es-form>
            <table>
              <tbody>
                <tr>
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Job Type
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div class="control">
                      <div class="">
                        {{ processSchedule[0].job.jobType }}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Status
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div class="control">
                      <div
                        class="buttons has-addons"
                        v-if="editCurrentPanel"
                      >
                        <button
                          class="button button--form"
                          v-for="(option, index) in statusOptions"
                          :key="index"
                          @click="status = option.code"
                          :class="{ 'is-active': status == option.code }"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else>
                        {{ processSchedule[0].processSchedule.status ? 'Active' : 'Inactive' }}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr>
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Near Match Age
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div class="control is-half-width" v-if="editCurrentPanel">
                      <input
                        class="input is-half-width"
                        type="text"
                        :readonly="!editCurrentPanel"
                        :class="{ 'is-static': !editCurrentPanel }"
                        maxlength="4"
                        v-model="nearMatchAge"
                        aria-label="Near Match Age"
                      > days
                    </div>
                    <div v-else>
                      {{ processSchedule[0].processSchedule.nearMatchAge }} days
                    </div>
                  </td>
                </tr>
                <tr>
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Frequency Type
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div
                      class="control buttons has-addons"
                      v-if="editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="option in frequencyOptions"
                        :key="option.description"
                        @click.prevent="selectFrequencyType(option)"
                        :class="{ 'is-active': frequencyType === option.description }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                    <div v-else>
                      {{ processSchedule[0].schedule.frequencyType }}
                    </div>
                  </td>
                </tr>
                <tr
                  v-if="frequencyType !== 'Daily'">
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Frequency Details
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div
                      class="control buttons has-addons"
                      v-if="frequencyType === 'Weekly' && editCurrentPanel"
                    >
                      <button
                        class="button button--form"
                        v-for="day in ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']"
                        :key="day"
                        @click="updateWeeklyFrequency(day)"
                        :class="{ 'is-active': day === weeklyFrequencyDetail }"
                      >
                        {{ day }}
                      </button>
                    </div>
                    <div
                      v-if="frequencyType === 'Monthly' && editCurrentPanel"
                      class="select is-half-width is-pulled-left"
                    >
                      <select
                        class="input"
                        v-model="monthlyFrequencyDetail"
                        aria-label="Select Day"
                        title="Frequency Details"
                      >
                        <option
                          v-for="(frequency, index) in getFrequencyDetailOptions()"
                          :key="index"
                          :value="frequency.value"
                        >
                          {{ frequency.label }}
                        </option>
                      </select>
                    </div>
                    <div
                      v-if="frequencyType === 'Yearly' && editCurrentPanel"
                      class="select is-pulled-left is-flex"
                      style="margin-right: 1rem"
                    >
                      <select
                        class="input"
                        v-model="yearlyFrequencyDetailMonth"
                        aria-label="Select Month"
                      >
                        <option
                          v-for="(month, index) in monthOptions"
                          :key="index"
                          :value="month.value"
                        >
                          {{ month.label }}
                        </option>
                      </select>
                    </div>
                    <div
                      v-if="frequencyType === 'Yearly' && editCurrentPanel"
                      class="select is-pulled-left is-flex"
                    >
                      <select
                        class="input"
                        v-model="monthlyFrequencyDetail"
                        aria-label="Select Day"
                      >
                        <option
                          v-for="(frequency, index) in getFrequencyDetailOptions()"
                          :key="index"
                          :value="frequency.value"
                        >
                          {{ frequency.label }}
                        </option>
                      </select>
                    </div>
                    <div v-if="!editCurrentPanel">
                      {{ frequencyValue() }}
                    </div>
                  </td>
                </tr>
                <tr>
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Start Time
                  </th>
                  <td
                    class="no-border selections"
                    :class="{ 'no-border': editCurrentPanel }"
                    v-if="editCurrentPanel"
                  >
                    <div class="select">
                      <select
                        class="input"
                        v-model="hourOption"
                        aria-label="Select Hour"
                      >
                        <option
                          v-for="(hour, index) in getHourOptions()"
                          :key="index"
                          :value="hour"
                        >
                          {{ hour }}
                        </option>
                      </select>
                    </div>
                    <strong style="margin: 0 5px;">:</strong>
                    <div class="select">
                      <select
                        class="input"
                        v-model="minuteOption"
                        aria-label="Select Minute"
                      >
                        <option
                          v-for="(minute, index) in getMinuteOptions()"
                          :key="index"
                          :value="minute"
                        >
                          {{ minute }}
                        </option>
                      </select>
                    </div>
                    <div style="margin: 0 1rem;">
                      <button
                        class="button button--form"
                        v-for="option in amPmOptions"
                        :key="option.description"
                        @click="amPmType = option.description"
                        :class="{ 'is-active': amPmType === option.description }"
                      >
                        {{ option.description }}
                      </button>
                    </div>
                  </td>
                  <td v-else>
                    {{ formattedTime(processSchedule[0].schedule.startTime) }}
                  </td>
                </tr>
                <tr>
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Notification
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div class="control">
                      <div
                        class="buttons has-addons"
                        v-if="editCurrentPanel"
                      >
                        <button
                          class="button button--form"
                          v-for="(option, index) in notificationOptions"
                          :key="index"
                          @click="notification = option.description"
                          :class="{ 'is-active': notification === option.description }"
                        >
                          {{ option.description }}
                        </button>
                      </div>
                      <div v-else>
                        {{ notification }}
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="notification === 'Enabled'">
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Send To
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div class="control">
                      <div class="">
                        State Super Users
                        <!--{{ processSchedule[0].notification.sendTo }}-->
                      </div>
                    </div>
                  </td>
                </tr>
                <tr v-if="notification === 'Enabled'">
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Additional Email (optional)
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div class="control" v-if="editCurrentPanel">
                      <input
                        class="input has-text-grey is-italic"
                        type="text"
                        :readonly="!editCurrentPanel"
                        :class="{ 'is-static': !editCurrentPanel }"
                        maxlength="250"
                        v-model="additionalEmail"
                        placeholder="sample@email.com"
                        aria-label="Additional Email (optional)"
                      >
                    </div>
                    <div v-else class="is-italic has-text-grey">
                      {{ processSchedule[0].notification.additionalEmail || 'N/A' }}
                    </div>
                  </td>
                </tr>
                <tr v-if="notification === 'Enabled'">
                  <th
                    class="has-text-grey table-header-normal"
                    :class="{ 'no-border': editCurrentPanel }"
                  >
                    Email Template
                  </th>
                  <td :class="{ 'no-border': editCurrentPanel }">
                    <div class="control">
                      <div v-if="editCurrentPanel && changeFile"
                        :class="{ 'no-temp': !editCurrentPanel }"
                        class="
                          is-flex
                          flex-direction--column
                          flex-justify--start
                          file
                          has-name
                          is-right
                          is-fullwidth
                        "
                      >
                        <es-email-template-upload-input
                          input-id="id"
                          input-ref="inputFile"
                          :has-error-file="hasErrorFile"
                          @validFileExtensions="setErrors($event)"
                          :selected-files="files"
                        />
                        <div v-if="hasErrorFile" class="help is-danger">
                          {{ errorMsgFile }}
                        </div>
                      </div>
                      <div v-else>
                        <div>
                          {{ processSchedule[0].notification.template }}
                          <a
                            v-if="editCurrentPanel"
                            @click="changeFile = true"
                            role="button"
                            tabindex="0"
                            @keyup.enter="changeFile = true"
                          >
                            Change
                          </a>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </template>
        </es-form>
        <div v-if="editCurrentPanel"
          class="is-flex flex-justify--end flex-align--center"
          style="margin-right: 2rem; padding-bottom: 2rem"
        >
          <button
            class="button button-link has-underline"
            @click.prevent="cancelPanel()"
            style="margin-right: 1rem"
          >
            Cancel
          </button>
          <button
            class="button is-primary"
            @click.prevent="checkRequiredFields"
          >
            Update
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faEdit,
  faTrashAlt,
} from '@fortawesome/pro-regular-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faCog } from '@fortawesome/pro-solid-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsEmailTemplateUploadInput from '@/components/EsEmailTemplateUploadInput.vue';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleLeft,
  faEdit,
  faCog,
  faTrashAlt,
  faTimes,
);

export default {
  mixins: [
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
    EsEmailTemplateUploadInput,
  },
  watch: {
    hourOption() {
      this.calculateTime();
    },
    minuteOption() {
      this.calculateTime();
    },
    amPmType() {
      this.calculateTime();
    },
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
      activeModules: 'appModules/activeModules',
      allModules: 'appModules/getAllModules',
      properties: 'propertiesModule/properties',
    }),
    formattedTime() {
      return (time) => {
        const date = new Date();
        date.setHours(Math.floor(time / 100));
        date.setMinutes(time % 100);
        return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
      };
    },
    formDataBody() {
      const formData = new FormData();
      formData.append('nearMatchAge', this.nearMatchAge);
      formData.append('frequencyMonthOfYear', this.yearlyFrequencyDetailMonth);
      formData.append('status', this.status);
      formData.append('jobId', this.processSchedule[0].job.id);
      formData.append('jobType', 'Cancel Near Matches');
      formData.append('jobLevel', 'State');
      formData.append('jobTriggerType', 'Pending Near Match');
      formData.append('notificationId', this.processSchedule[0].schedule.notificationId);
      formData.append('notificationActive', (this.notification === 'Enabled'));
      formData.append('severity', 'Medium');
      formData.append('sendTo', 'State Super Users');
      formData.append('additionalEmail', this.additionalEmail);
      if (this.files[0] && this.files[0].name) {
        const selectedTemplateName = this.files[0].name;
        formData.append('template', selectedTemplateName);
        formData.append('templateContent', this.files[0]);
      } else {
        formData.append('template', this.processSchedule[0].notification.template);
        // don't set the templateContent if file is not selected.
      }
      formData.append('scheduleId', this.processSchedule[0].schedule.id);
      formData.append('frequencyType', this.frequencyType);
      formData.append('frequencyDayOfWeek', this.weeklyFrequencyDetail);
      formData.append('frequencyDayOfMonth', this.monthlyFrequencyDetail);
      formData.append('startTime', this.startTime);

      if ((this.activeModule && this.activeModule.name) === 'personId') {
        formData.append('scheduleType', 'PID');
      } else if ((this.activeModule && this.activeModule.name) === 'directMatch') {
        formData.append('scheduleType', 'DM');
      } else if ((this.activeModule && this.activeModule.name) === 'locationID') {
        formData.append('scheduleType', 'LOC');
      }



      return formData;
    },
  },
  data() {
    return this.initialData();
  },
  created() {
    this.onLoad();
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    this.monthOptions = months.map((month, index) => ({
      value: index + 1,
      label: month,
    }));
    this.frequencyOptions = [
      {
        description: 'Daily',
      },
      {
        description: 'Weekly',
      },
      {
        description: 'Monthly',
      },
      {
        description: 'Yearly',
      },
    ];
    this.amPmOptions = [
      {
        description: 'AM',
      },
      {
        description: 'PM',
      },
    ];
    this.statusOptions = [
      {
        code: true,
        description: 'Active',
      },
      {
        code: false,
        description: 'Inactive',
      },
    ];
    this.notificationOptions = [
      {
        description: 'Enabled',
      },
      {
        description: 'Disabled',
      },
    ];
  },
  methods: {
    initialData() {
      return {
        editCurrentPanel: false,
        loading: false,
        loadingError: false,
        hasErrorFile: false,
        changeFile: false,

        hourOption: null,
        minuteOption: null,
        amPmType: 'AM',
        monthlyFrequencyDetail: null,
        weeklyFrequencyDetail: null,
        yearlyFrequencyDetailMonth: null,

        processSchedule: [],

        jobType: 'Cancel Near Matches',
        jobLevel: 'State',
        jobTriggerType: 'Pending Near Match',
        severity: 'Medium',
        sendTo: 'State Super Users',
        additionalEmail: '',
        template: '',
        templateContent: '',
        frequencyType: '',
        startTime: '',
        nearMatchAge: '45',
        status: true,
        files: [],
        notification: 'Enabled',
      };
    },
    async updatePanel() {
      try {
        this.loading = true;
        await axios({
          method: 'put',
          url: `process-schedules/${this.processSchedule[0].processSchedule.id}`,
          data: this.formDataBody,
        });
        this.goBackToNotifications();
        this.showNotification('success', ['The Near Match updated successfully.']);
      } catch (e) {
        this.showNotification('danger', ['The Notification could not be updated.']);
      } finally {
        this.loading = false;
      }
    },
    frequencyValue() {
      if (this.processSchedule[0].schedule.frequencyType === 'Daily') {
        return this.formattedTime(this.processSchedule[0].schedule.startTime);
      } if (this.processSchedule[0].schedule.frequencyType === 'Weekly') {
        return this.processSchedule[0].schedule.frequencyDayOfWeek;
      } if (this.processSchedule[0].schedule.frequencyType === 'Monthly') {
        return this.formatDayOfMonth(this.processSchedule[0].schedule.frequencyDayOfMonth);
      } if (this.processSchedule[0].schedule.frequencyType === 'Yearly') {
        return `${this.getMonthName(this.processSchedule[0].processSchedule.frequencyMonthOfYear)}
          ${this.formatDayOfMonth(this.processSchedule[0].schedule.frequencyDayOfMonth)}`;
      }
      return '';
    },
    calculateTime() {
      const hour = parseInt(this.hourOption, 10);
      const minute = parseInt(this.minuteOption, 10);
      let updatedHour = hour;
      if (this.amPmType === 'PM' && hour < 12) {
        updatedHour += 12;
      } else if (this.amPmType === 'AM' && hour === 12) {
        updatedHour = 0;
      }
      const time = updatedHour * 100 + minute;
      this.startTime = time;
    },
    getHourOptions() {
      const options = [];
      for (let i = 1; i <= 12; i += 1) {
        options.push(i.toString());
      }
      return options;
    },
    getMinuteOptions() {
      const options = [];
      for (let i = 0; i <= 59; i += 1) {
        options.push(i.toString().padStart(2, '0'));
      }
      return options;
    },
    getFrequencyDetailOptions() {
      const options = [];
      for (let i = 1; i <= 31; i += 1) {
        let suffix = 'th';
        if (i === 1 || i === 21 || i === 31) {
          suffix = 'st';
        } else if (i === 2 || i === 22) {
          suffix = 'nd';
        } else if (i === 3 || i === 23) {
          suffix = 'rd';
        }
        options.push({
          value: i,
          label: `${i}${suffix}`,
        });
      }
      return options;
    },
    formatDayOfMonth(day) {
      const parsedDay = parseInt(day, 10);
      let suffix = 'th';
      if (parsedDay === 1 || parsedDay === 21 || parsedDay === 31) {
        suffix = 'st';
      } else if (parsedDay === 2 || parsedDay === 22) {
        suffix = 'nd';
      } else if (parsedDay === 3 || parsedDay === 23) {
        suffix = 'rd';
      }
      return `${parsedDay}${suffix}`;
    },
    getMonthName(monthNum) {
      const date = new Date();
      date.setMonth(monthNum - 1);
      return date.toLocaleString('default', { month: 'long' });
    },
    updateWeeklyFrequency(day) {
      this.weeklyFrequencyDetail = day;
    },
    selectFrequencyType(option) {
      this.frequencyType = option.description;
      if (option.description !== 'Weekly') {
        this.weeklyFrequencyDetail = '';
      }
      if (option.description !== 'Monthly') {
        this.monthlyFrequencyDetail = '';
      }
      if (option.description !== 'Yearly') {
        this.yearlyFrequencyDetail = '';
      }
    },
    cancelPanel() {
      this.editCurrentPanel = !this.editCurrentPanel;
      this.changeFile = false;
      this.hasErrorFile = false;
      this.errorMsgFile = null;
      this.frequencyType = this.processSchedule[0].schedule.frequencyType;
      this.notification = 'Enabled';
      this.hideNotification();
    },
    goBackToNotifications() {
      this.$emit('viewNearMatchProcessingDetails');
    },
    editPanel() {
      this.editCurrentPanel = !this.editCurrentPanel;
      this.frequencyType = this.processSchedule[0].schedule.frequencyType;
      this.hideNotification();
    },
    checkRequiredFields() {
      if (!this.processSchedule[0].notification.template && !this.files.length) {
        this.hasErrorFile = true;
        this.errorMsgFile = 'No file chosen. A File is required.';
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      } else if (this.files.length) {
        if (!this.hasErrorFile) {
          this.updatePanel();
        } else {
          this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
        }
      } else {
        this.updatePanel();
      }
    },
    setErrors(value) {
      if (!value) {
        this.hasErrorFile = true;
        this.errorMsgFile = 'File extension is invalid. Valid extensions are \'.txt\' or \'.csv\' or \'.TXT\' or \'.CSV\'';
      } else {
        this.clearErrors();
      }
    },
    clearErrors() {
      this.hideNotification();
      this.hasErrorFile = false;
      this.errorMsgFile = null;
    },
    getProcessSchedule() {
      if ((this.activeModule && this.activeModule.name) === 'personId') {
        return axios.get('process-schedules/list/pid');
      // eslint-disable-next-line no-else-return
      } else if ((this.activeModule && this.activeModule.name) === 'directMatch') {
        return axios.get('process-schedules/list/dm');
      } else if ((this.activeModule && this.activeModule.name) === 'locationID') {
        return axios.get('process-schedules/list/loc');
      } else {
        return axios.get('process-schedules');
      }
    },
    async onLoad() {
      try {
        this.loading = true;
        this.loadingError = false;
        const response = await
        this.getProcessSchedule();
        this.processSchedule = response.data;
        this.frequencyType = this.processSchedule[0].schedule.frequencyType;
        this.status = this.processSchedule[0].processSchedule.status;
        this.notification = ((!this.processSchedule[0].notification.sendTo) || this.processSchedule[0].notification.sendTo === '' || this.processSchedule[0].notification.sendTo === ' ') ? 'Disabled' : 'Enabled';
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    vertical-align: middle
  table td
    vertical-align: middle
    padding-left: 0
    &:first-child
      width: 30%
  .header
    display: flex
    align-items: center
  .no-border
    border: 0
  .property
    padding-left: 0
    font-size: 12px
    width: 70%
  .icons
    margin-left: auto
    width: 100px
    display: flex
    justify-content: space-around
    padding-right: 1rem
  .no-temp
    pointer-events: none
  .no-padding
    padding-left: 0 !important
  .modal-body
    margin-bottom: 1rem
    display: flex
    justify-content: flex-start
  .modal-card-body
    border-bottom-right-radius: .4rem
    border-bottom-left-radius: .4rem
    text-align: left
  .modal-card-head
    background-color: #F5F5F5
    text-align: left
  .modal-card-title
    margin-bottom: 0 !important
    color: #444
    font-weight: 700
  .update-box
    display: flex
    align-items: center
    justify-content: flex-end
    button
      margin-left: 1rem
  .selections
    display: flex
    align-items: center
  .select:not
    right: 0em
  .padding-right
    padding-right: 1rem
</style>
